import { useEffect, useRef } from 'react';

/**
 * Custom hook to dynamically update app branding based on fleet code
 * Updates manifest, favicon, theme color, and other meta tags
 * 
 * Uses /branding/{fleetCode}/ as the source, with fallback to /branding/default/
 * 
 * @param {string|null} fleetCode - The fleet code (e.g., "JYLT", "HAMR") or null for default
 * @param {Function} onBrandingReady - Optional callback when branding is applied
 */
export const useDynamicBranding = (fleetCode, onBrandingReady = null) => {
  const previousFleetCode = useRef(null);
  const brandingAppliedRef = useRef(false);

  useEffect(() => {
    // Normalize fleet code (uppercase, null if empty)
    const normalizedCode = fleetCode?.toUpperCase().trim() || null;
    
    // Skip if fleet code hasn't changed
    if (normalizedCode === previousFleetCode.current) {
      return;
    }
    
    previousFleetCode.current = normalizedCode;
    
    // Determine manifest path with fallback logic
    // Try fleet-specific manifest first, then fall back to default.
    // NOTE: Branding assets live under /public/branding/<FLEET>/... (matches index.html
    // and scripts/generate-icons.js).
    const determineManifestPath = async () => {
      const defaultPath = '/branding/default';
      
      // If no fleet code, use default
      if (!normalizedCode) {
        return defaultPath;
      }
      
      // Check if fleet-specific manifest exists
      const fleetPath = `/branding/${normalizedCode}`;
      try {
        const response = await fetch(`${fleetPath}/manifest.json`, { method: 'HEAD' });
        if (response.ok) {
          console.log(`[Branding] Fleet-specific manifest found: ${fleetPath}`);
          return fleetPath;
        } else {
          // 404 or other error - fall back to default
          console.log(`[Branding] Fleet manifest not found (${response.status}), falling back to default: ${defaultPath}`);
          return defaultPath;
        }
      } catch (error) {
        // Network error or CORS - fall back to default
        console.log(`[Branding] Error checking fleet manifest, falling back to default: ${defaultPath}`, error);
        return defaultPath;
      }
    };
    
    // Determine the manifest path and update branding
    determineManifestPath().then(manifestPath => {
      console.log('Updating branding for fleet:', normalizedCode || 'default', 'Path:', manifestPath);
    
    // Helper function to safely update or create link element
    const updateLinkElement = (selector, href, rel, type = null, sizes = null) => {
      let link = document.querySelector(selector);
      
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        if (type) link.type = type;
        if (sizes) link.sizes = sizes;
        document.head.appendChild(link);
      }
      
      link.href = href;
      return link;
    };
    
    // Helper function to safely update or create meta element
    const updateMetaElement = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      
      meta.content = content;
      return meta;
    };
    
      try {
        // Update manifest
        updateLinkElement(
          'link[rel="manifest"]',
          `${manifestPath}/manifest.json`,
          'manifest'
        );
        
        // Update favicon (multiple formats for browser compatibility)
        updateLinkElement(
          'link[rel="icon"][type="image/x-icon"]',
          `${manifestPath}/icons/favicon.ico`,
          'icon',
          'image/x-icon'
        );
        
        updateLinkElement(
          'link[rel="icon"][type="image/png"][sizes="192x192"]',
          `${manifestPath}/icons/android-chrome-192x192.png`,
          'icon',
          'image/png',
          '192x192'
        );
        
        updateLinkElement(
          'link[rel="icon"][type="image/png"][sizes="32x32"]',
          `${manifestPath}/icons/favicon-32x32.png`,
          'icon',
          'image/png',
          '32x32'
        );
        
        updateLinkElement(
          'link[rel="icon"][type="image/png"][sizes="16x16"]',
          `${manifestPath}/icons/favicon-16x16.png`,
          'icon',
          'image/png',
          '16x16'
        );
        
        // Update apple touch icon
        updateLinkElement(
          'link[rel="apple-touch-icon"]',
          `${manifestPath}/icons/apple-touch-icon.png`,
          'apple-touch-icon',
          null,
          '180x180'
        );
        
        // For default branding (no fleet code), notify immediately since links are already set in HTML
        if (!normalizedCode && onBrandingReady && typeof onBrandingReady === 'function') {
          // Default branding is ready immediately (links already in HTML)
          setTimeout(() => {
            onBrandingReady(true);
          }, 50); // Small delay to ensure DOM updates are complete
        }
        
        // Load manifest to get theme color
        fetch(`${manifestPath}/manifest.json`)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Failed to load manifest: ${response.status}`);
          }
          const ct = (response.headers.get('content-type') || '').toLowerCase();
          if (ct.includes('json')) {
            return response.json();
          }
          const text = await response.text();
          const trimmed = text.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            return JSON.parse(trimmed);
          }
          throw new Error('Manifest URL returned HTML or non-JSON (add public/branding/{fleet}/manifest.json)');
        })
          .then(manifest => {
            // Update theme color from manifest
            if (manifest.theme_color) {
              updateMetaElement('theme-color', manifest.theme_color);
            }
            
            // Update app name/title if needed
            if (manifest.name) {
              document.title = manifest.name;
            }
            
            console.log('Branding updated successfully:', {
              fleetCode: normalizedCode || 'default',
              manifestPath: manifestPath,
              themeColor: manifest.theme_color,
              appName: manifest.name
            });
            
            // Mark branding as applied and notify callback
            if (onBrandingReady && typeof onBrandingReady === 'function') {
              onBrandingReady(true);
            }
          })
          .catch((error) => {
            console.warn('Branding manifest unavailable, using defaults:', error.message || error);
            // Fallback to default theme color
            updateMetaElement('theme-color', '#1e40af');
            
            // Still notify callback (using defaults)
            if (onBrandingReady && typeof onBrandingReady === 'function') {
              onBrandingReady(true);
            }
          });
        
      } catch (error) {
        console.error('Error updating branding:', error);
        // Fallback to default theme color
        updateMetaElement('theme-color', '#1e40af');
        
        if (onBrandingReady && typeof onBrandingReady === 'function') {
          onBrandingReady(true);
        }
      }
    });
    
    // Cleanup function (optional - not strictly necessary for link elements)
    return () => {
      // No cleanup needed - we want the branding to persist
    };
  }, [fleetCode]);
};

export default useDynamicBranding;

